<template>
  <b-overlay
    :show="loading"
    spinner-variant="primary"
    variant="transparent"
    rounded="sm"
  >
    <div class="d-flex justify-content-between align-items-center mb-50">
      <span class="font-weight-bold">Rate periods</span>
      <b-button
        size="sm"
        variant="outline-primary"
        :disabled="!!form"
        @click="startAdd"
      >
        Add rate
      </b-button>
    </div>
    <b-table-lite
      v-if="rows.length"
      :items="rows"
      :fields="fields"
      small
      bordered
      responsive
      class="mb-1"
    >
      <template #cell(valid_from)="data">
        {{ monthLabel(data.value) }}
      </template>
      <template #cell(valid_to)="data">
        {{ data.value ? monthLabel(data.value) : 'open' }}
      </template>
      <template #cell(target_minutes)="data">
        {{ data.value ? formatHm(data.value) : '–' }}
      </template>
      <template #cell(overtime_start_minutes)="data">
        {{ data.value === null ? '–' : formatHm(data.value) }}
      </template>
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <b-button
            variant="flat"
            class="btn-icon p-25"
            :disabled="!!form"
            @click="startEdit(data.item)"
          >
            <feather-icon icon="Edit2Icon" />
          </b-button>
          <!-- the earliest period stays: earlier months would fall back to today's rate -->
          <b-button
            v-if="data.index < rows.length - 1"
            variant="flat"
            class="btn-icon p-25"
            :disabled="!!form"
            @click="remove(data.item)"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </div>
      </template>
    </b-table-lite>
    <p
      v-else
      class="text-muted small"
    >
      No rate periods yet.
    </p>
    <employee-rate-form
      v-if="form"
      :key="form.id || 'new'"
      :initial="form"
      :saving="saving"
      @save="save"
      @cancel="form = null"
    />
  </b-overlay>
</template>

<script>
import { BButton, BOverlay, BTableLite } from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatHm } from '@/utils/hoursMinutes'
import EmployeeRateForm from './EmployeeRateForm.vue'

const API = `${process.env.VUE_APP_ROOT_API}/employee-rates/`

function currentMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default {
  components: {
    BButton, BOverlay, BTableLite, EmployeeRateForm,
  },
  props: {
    employeeId: { type: [Number, String], required: true },
  },
  data() {
    return {
      rows: [],
      loading: false,
      saving: false,
      // { id (null = new), ...EmployeeRateForm `initial` }
      form: null,
      fields: [
        { key: 'valid_from', label: 'Valid from' },
        { key: 'valid_to', label: 'Valid to' },
        { key: 'hourly_rate', label: 'Hourly rate' },
        { key: 'monthly_salary', label: 'Fixed salary' },
        { key: 'employer_factor', label: 'Employer factor' },
        { key: 'target_minutes', label: 'Target h/month' },
        { key: 'overtime_start_minutes', label: 'Overtime at start' },
        { key: 'actions', label: '' },
      ],
    }
  },
  created() {
    this.load()
  },
  methods: {
    headers() {
      return { headers: { Authorization: `JWT ${this.$store.state.jwt}` } }
    },
    async load() {
      this.loading = true
      try {
        const { data } = await axios.get(API, { params: { employee: this.employeeId }, ...this.headers() })
        this.rows = data
      } catch (error) {
        this.fail(error, 'Could not load rate periods.')
      } finally {
        this.loading = false
      }
    },
    formatHm,
    startAdd() {
      // a new period usually changes one amount of the latest one; the opening balance is entered once
      const latest = this.rows[0] || {
        hourly_rate: '0', monthly_salary: '0', employer_factor: '1', target_minutes: 0,
      }
      this.form = {
        id: null,
        month: currentMonth(),
        hourly_rate: latest.hourly_rate,
        monthly_salary: latest.monthly_salary,
        employer_factor: latest.employer_factor,
        target: latest.target_minutes ? formatHm(latest.target_minutes) : '',
        opening: '',
      }
    },
    startEdit(row) {
      this.form = {
        id: row.id,
        month: row.valid_from.slice(0, 7),
        hourly_rate: row.hourly_rate,
        monthly_salary: row.monthly_salary,
        employer_factor: row.employer_factor,
        target: row.target_minutes ? formatHm(row.target_minutes) : '',
        opening: formatHm(row.overtime_start_minutes),
      }
    },
    async save(payload) {
      this.saving = true
      try {
        const { data } = this.form.id
          ? await axios.patch(`${API}${this.form.id}/`, payload, this.headers())
          : await axios.post(API, { ...payload, employee: this.employeeId }, this.headers())
        this.form = null
        this.toast('success', this.doneText('Rate saved.', data))
        await this.changed()
      } catch (error) {
        this.fail(error, 'Could not save the rate.')
      } finally {
        this.saving = false
      }
    },
    async remove(row) {
      const result = await this.$swal({
        title: 'Delete this rate period?',
        text: `${this.monthLabel(row.valid_from)}: the previous period will apply instead.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
      })
      if (!result.value) return
      try {
        const { data } = await axios.delete(`${API}${row.id}/`, this.headers())
        this.toast('success', this.doneText('Rate period deleted.', data))
        await this.changed()
      } catch (error) {
        this.fail(error, 'Could not delete the rate period.')
      }
    },
    async changed() {
      await this.load()
      const month = `${currentMonth()}-01`
      this.$emit('changed', this.rows.find(r => r.valid_from <= month) || null)
    },
    fail(error, fallback) {
      if (error.response && error.response.status === 401) {
        this.$router.push({ name: 'login' })
        return
      }
      const data = (error.response && error.response.data) || {}
      const first = data.detail || Object.values(data).flat()[0]
      this.toast('danger', typeof first === 'string' ? first : fallback)
    },
    doneText(text, data) {
      return data.rebuild_queued ? `${text} Staff costs are being recalculated.` : text
    },
    toast(variant, title) {
      this.$toast({ component: ToastificationContent, props: { title, icon: 'BellIcon', variant } })
    },
    monthLabel(isoDate) {
      const [y, m] = isoDate.split('-').map(Number)
      return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    },
  },
}
</script>
